import { AsyncPipe } from '@angular/common';
import { Component, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	Organization,
	OrganizationOption,
} from '@context/shared/types/organization';
import { TranslocoModule } from '@jsverse/transloco';
import {
	TuiButton,
	TuiDataList,
	TuiFilterByInputPipe,
	TuiInput,
	TuiLoader,
} from '@taiga-ui/core';
import {
	TuiButtonLoading,
	TuiComboBox,
	TuiDataListWrapper,
} from '@taiga-ui/kit';
import {
	catchError,
	finalize,
	map,
	Observable,
	of,
	startWith,
	tap,
} from 'rxjs';
import { AuthService } from '../../services';

/**
 * Reusable organization selector (combo box + select button).
 * Fetches organizations from the backend (GET /v1/users/me/organizations) internally;
 * data returned is determined by the authenticated user. Parent handles selection by
 * calling AuthService.selectOrganization() and then navigating or closing a dialog.
 */
@Component({
	standalone: true,
	imports: [
		TranslocoModule,
		AsyncPipe,
		ReactiveFormsModule,
		TuiComboBox,
		TuiDataList,
		TuiDataListWrapper,
		TuiFilterByInputPipe,
		TuiInput,
		TuiButton,
		TuiButtonLoading,
		TuiLoader,
	],
	selector: 'ctx-organization-selector',
	templateUrl: 'organization-selector.component.html',
	styleUrl: 'organization-selector.component.scss',
})
export class OrganizationSelectorComponent {
	private readonly authService = inject(AuthService);

	/** True while organizations are being loaded from the API. */
	readonly loading = signal(false);

	/** Loaded via API; data is determined by the authenticated user. */
	readonly organizationOptions$: Observable<OrganizationOption[]> =
		this.authService.getMyOrganizations().pipe(
			startWith([]),
			tap(() => this.loading.set(true)),
			map((orgs) => orgs.map((org) => new OrganizationOption(org, null))),
			catchError(() => of([])),
			finalize(() => this.loading.set(false)),
		);

	/** True while selection is in progress (e.g. switching org). */
	readonly disabled = input(false);

	/** Emitted when user confirms selection; parent should call selectOrganization and handle success. */
	readonly organizationSelected = output<Organization & { id: string }>();

	readonly orgControl = new FormControl<OrganizationOption | null>(null);

	get canSelect(): boolean {
		const value = this.orgControl.value;
		return !!value?.data && !this.disabled();
	}

	onSelect(): void {
		const value = this.orgControl.value;
		if (!value?.data) return;
		this.organizationSelected.emit(
			value.data as Organization & { id: string },
		);
	}
}
